<script lang="ts" module>
	import * as _ from 'radashi'
	import { attrs } from '#library/attrs.ts'
	import type {
		Blog,
		Blok,
		Gallery,
		News,
		Postdex,
		Video,
		Event,
		StoryblokAsset
	} from '#library/components.schema.build.ts'
	import type { Event as EventStoryblok, Member, Alumni, Page, News as NewsStoryblok } from '#library/types.ts'
	import CardNews from '#components/card_news.svelte'
	import CardThought from '#components/card_thought.svelte'
	import Pagination from '#components/pagination.svelte'
	import Filterbar from '#components/filterbar.svelte'
	import { format_date } from 'kitto'
	import { goto } from '$app/navigation'
	import { params } from '#library/params.svelte.ts'
	import CardEvent from '#components/card_event.svelte'
	import CardAction from '#components/card_action.svelte'
	import type { ISbStories, ISbStoriesParams, ISbStoryData } from '@storyblok/svelte'
	import { scrollto } from '#library/scrollto.ts'

	const DEFAULT_PER_PAGE = 15

	/** What this block's `load` attaches to the blok at request time. */
	type PostdexData = {
		posts?: Array<ISbStoryData<Alumni | News | Event | Blog | Gallery | Video>>
		pagination?: { page: number; per_page: number; total: number }
		tags?: Array<{ name: string; taggings_count: number }>
		filters?: Array<{ label: string; href: string; active: boolean }> | false
	}

	// results go on the blok, not module scope. Module state is shared by every
	// request the server process handles, so with page/search/type/year read off
	// the URL, whichever request happened to warm the instance decided what
	// everyone else was served — filters and search appeared to be ignored.
	// `story` is per-request, so writing there is safe.
	export const load: Load<Page> = async ({ story, api, version, event: { url } }) => {
		const postdex = story.content.blocks?.find(b => b.component === 'postdex') as
			| (Postdex & PostdexData)
			| undefined
		if (!postdex) return

		const per_page = url.searchParams.get('per_page') ?? DEFAULT_PER_PAGE
		const page = url.searchParams.get('page') ?? 1
		const search = url.searchParams.get('search')
		const year = url.searchParams.get('year')
		const type = url.searchParams.get('type')?.toString()

		const options: ISbStoriesParams = {
			version,
			content_type: type,
			per_page: Number(per_page),
			page: Number(page),
			is_startpage: false,
			search_term: search?.trim(),
			with_tag: url.searchParams.get('category')?.toString(),
			first_published_at_lt: year ? `${year.slice(1)}-12-31 23:59` : undefined,
			sort_by: search ? undefined : 'first_published_at:desc'
		}

		switch (postdex.type) {
			case 'thoughts':
				options.starts_with = 'journal/thought-leadership/'
				options.excluding_slugs = 'journal/podcast'
				break

			case 'news':
				options.starts_with = 'journal/'
				options.excluding_slugs = 'journal/thought-leadership/*,journal/podcast'
				break

			case 'events': {
				options.starts_with = 'whats-on/'
				options.content_type = 'event'

				if (!search) options.sort_by = 'sort_by_date'
				if (type) options.filter_query = { category: { in: type } }
				break
			}

			case 'alumni':
				options.starts_with = 'community/alumni-hub/alumni-profiles/'
				options.content_type = 'alumni'
				options.sort_by = search ? undefined : 'name:asc'
				break

			case 'staff':
				options.starts_with = 'community/our-staff/'
				options.content_type = 'member'
				options.sort_by = search ? undefined : 'name:asc'
				break
		}

		// the posts query, the tag list and (for events) the full event list used to
		// build the category filters are independent of each other. They ran in
		// sequence, so the block cost their sum rather than the slowest of them.
		const [result, all_tags, all_events] = await Promise.all([
			api.get('cdn/stories', options) as Promise<ISbStories<News | Blog | Gallery | Video | Event>>,
			api
				.get('cdn/tags', { version, per_page: 100 })
				.then(r => r.data.tags as Array<{ name: string; taggings_count: number }>),
			postdex.type === 'events'
				? api.getAll('cdn/stories', { version, per_page: 100, content_type: 'event' })
				: undefined
		])

		postdex.posts = result.data.stories
		const total = result.total

		switch (postdex.type) {
			case 'events': {
				const categories = _.unique(all_events ?? [], event => (event as EventStoryblok).content.category)
					.map(event => (event as EventStoryblok).content.category)
					.filter(Boolean)

				postdex.filters = [
					{
						label: 'All events',
						href: url.pathname,
						active: !url.searchParams.has('type')
					},
					...categories.map(category => ({
						label: category!,
						href: url.pathname + '?type=' + category,
						active: url.searchParams.get('type') === category
					}))
				]

				break
			}

			case 'alumni':
			case 'staff':
				postdex.filters = false
				break

			default: {
				postdex.filters = [
					{
						label: 'All posts',
						href: url.pathname,
						active: !url.searchParams.has('type')
					},
					{
						label: 'Blog',
						href: params({ type: 'blog', page: null }, url),
						active: url.searchParams.get('type') === 'blog'
					},
					{
						label: 'News',
						href: params({ type: 'news', page: null }, url),
						active: url.searchParams.get('type') === 'news'
					},
					{
						label: 'Galleries',
						href: params({ type: 'gallery', page: null }, url),
						active: url.searchParams.get('type') === 'gallery'
					},
					{
						label: 'Videos',
						href: params({ type: 'video', page: null }, url),
						active: url.searchParams.get('type') === 'video'
					}
				]
			}
		}

		postdex.pagination = {
			page: Number(page),
			per_page: Number(per_page),
			total
		}

		postdex.tags =
			postdex.type === 'staff'
				? all_tags.filter(tag => tag.name?.toLowerCase().startsWith('bedales'))
				: all_tags
	}
</script>

<script lang="ts">
	interface Props {
		blok: Blok<Postdex> & PostdexData
	}

	let { blok }: Props = $props()

	let form_state: 'idle' | 'loading' = $state('idle')
	let section: HTMLElement

	const categories = $derived.by(() => {
		switch (blok.type) {
			case 'alumni':
				return false

			case 'staff':
				return {
					'': 'All schools',
					...Object.fromEntries((blok.tags ?? []).map(tag => [tag.name, tag.name]))
				}

			default:
				return {
					'': 'All categories',
					...Object.fromEntries((blok.tags ?? []).map(tag => [tag.name, tag.name]))
				}
		}
	})

	const current = new Date().getFullYear()
	const years = Array.from({ length: current - 2022 + 1 }, (_, i) => current - i)
	const dates = $derived(
		blok.type !== 'staff' && {
			'': 'All dates',
			...Object.fromEntries(years.map(year => [`y${year}`, year]))
		}
	)

	// select full image-like object only if it has a truthy filename
	type WithFilename = Pick<StoryblokAsset, 'filename'>
	type PostContent = NonNullable<typeof blok.posts>[number]['content']

	function pick_image(content: PostContent): WithFilename | undefined {
		if (typeof content !== 'object' || content === null) return undefined

		if ('photo' in content) {
			const photo = (content as { photo?: StoryblokAsset | undefined }).photo
			if (photo?.filename) return photo
		}

		if ('image' in content) {
			const image = (content as { image?: StoryblokAsset | undefined }).image
			if (image?.filename) return image
		}

		return undefined
	}

	async function go(p: Record<string, string | number | null>): Promise<void> {
		await goto(params(p), { replace: true, reset: false })
		scrollto(blok.anchor!)
	}

	let event_view = $state<'grid' | 'calendar'>('grid')
</script>

<section class="postdex {blok.theme}" {...attrs(blok)} bind:this={section}>
	{#if blok.type !== 'thoughts'}
		<Filterbar
			bind:form_state
			bind:event_view
			filters={blok.filters ?? false}
			{categories}
			{dates}
			events={blok.type === 'events'}
		/>
	{/if}
	<div
		class="items contain {blok.type}"
		class:loading={form_state === 'loading'}
		class:calendar={event_view === 'calendar'}
		class:shrink={blok.shrink}
	>
		{#if blok.posts?.length}
			{#each blok.posts as post (post.uuid)}
				{@const image = pick_image(post.content)}
				{#if blok.type === 'thoughts'}
					{@const thought = post as NewsStoryblok}
					<CardThought
						link="/{thought.full_slug}"
						image={image as never}
						suptitle={format_date('{ddd}, {DD} {MMM} {YYYY}', post.first_published_at!)}
						title={thought.name}
						subtitle={thought.content.metatags?.description || ''}
						caption="Thought Leadership"
						body=""
						theme="theme-lupton-on-pollen"
					/>
				{:else if blok.type === 'events'}
					<CardEvent event={post as EventStoryblok} view={event_view} />
				{:else if blok.type === 'staff'}
					{@const member = post as Member}
					<CardAction
						link={{ url: `/${member.full_slug}` }}
						image={member.content.photo}
						title={member.name}
						subtitle={member.content.title}
						theme={member.content.theme}
					/>
				{:else if blok.type === 'alumni'}
					{@const alumni = post as Alumni}
					<CardAction
						link={{ url: `/${alumni.full_slug}` }}
						image={alumni.content.photo}
						title={alumni.name}
					/>
				{:else}
					{@const news = post as NewsStoryblok}
					<CardNews
						link="/{news.full_slug}"
						image={image as never}
						suptitle={format_date('{ddd}, {DD} {MMM} {YYYY}', news.first_published_at!)}
						title={news.name}
						subtitle={news.content.metatags?.description || ''}
						caption={news.content.component}
						theme="theme-lupton-on-pollen"
						type={news.content.component}
					/>
				{/if}
			{/each}
		{:else}
			<div class="empty">No posts found.</div>
		{/if}
	</div>
	<div class="pagi">
		{#key blok.pagination}
			{#if (blok.pagination?.total ?? 0) > (blok.pagination?.per_page ?? 1)}
				<Pagination
					active={(blok.pagination?.page ?? 1) - 1}
					items={Math.ceil((blok.pagination?.total ?? 0) / (blok.pagination?.per_page ?? 1))}
					onprev={() => go({ page: (blok.pagination?.page ?? 1) - 1 })}
					onnext={() => go({ page: (blok.pagination?.page ?? 1) + 1 })}
					onpage={page => go({ page: page + 1 })}
					numbers
				/>
			{/if}
		{/key}
	</div>
</section>

<style lang="css">
	.postdex {
		background-color: var(--c-background);
		color: var(--c-foreground);
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));
	}

	.items {
		display: grid;
		gap: 2rem;
		transition: opacity 0.3s ease-in-out;

		@media (--from-mobile) {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}

		@media (--from-laptop) {
			&.shrink {
				grid-template-columns: repeat(4, 1fr);
			}
		}

		&.thoughts {
			grid-template-columns: 1fr;
			gap: 4rem;
		}

		&.calendar {
			background-color: var(--c-white);
			gap: 0;
			grid-template-columns: 1fr;

			@media (--from-mobile) {
				padding: var(--s-64);
			}
		}

		&.loading {
			cursor: wait;
			opacity: 0.5 !important;
		}
	}

	.pagi {
		display: flex;
		justify-content: center;
		margin-block-start: 6rem;
	}
</style>
